// Multi-column sort order helper used by the Settings grids (Terminal
// colours, Date events, View templates) and the Vessels table.
// Tracks an ordered list of (key, dir) pairs
// — the first entry is the primary sort, subsequent entries are
// tiebreakers in order.
//
// The associated UI exposes per-column setSort/onRemoveSort actions
// and a priority badge index (1-based) shown next to the sort arrow
// when the order has more than one column.

import { createSignal, type Accessor } from "solid-js";

export interface SortEntry<K extends string> {
  key: K;
  dir: "asc" | "desc";
}

export interface SortOrder<K extends string> {
  order: Accessor<SortEntry<K>[]>;
  // Set / replace a column's direction. New columns append to the end
  // of the order (lowest priority); existing columns keep their
  // position and only update direction.
  setSort: (key: K, dir: "asc" | "desc") => void;
  // Remove a column from the sort entirely.
  removeSort: (key: K) => void;
  // Convenience accessors for ColumnHeader props.
  isActive: (key: K) => Accessor<boolean>;
  dirOf: (key: K) => Accessor<"asc" | "desc">;
  // 1-based priority position when the order has more than one
  // column, otherwise null. ColumnHeader uses this to show the small
  // index badge next to the arrow only when it actually disambiguates.
  indexOf: (key: K) => Accessor<number | null>;
  // Wholesale replace — for URL-sync consumers restoring a full order.
  replace: (next: SortEntry<K>[]) => void;
}

export function createSortOrder<K extends string>(
  initial: SortEntry<K>[],
): SortOrder<K> {
  const [order, setOrder] = createSignal<SortEntry<K>[]>(initial);

  function setSort(key: K, dir: "asc" | "desc") {
    const cur = order();
    const i = cur.findIndex((e) => e.key === key);
    if (i >= 0) {
      // Update direction in place.
      const next = cur.slice();
      next[i] = { key, dir };
      setOrder(next);
    } else {
      setOrder([...cur, { key, dir }]);
    }
  }

  function removeSort(key: K) {
    setOrder(order().filter((e) => e.key !== key));
  }

  return {
    order,
    setSort,
    removeSort,
    replace: (next) => setOrder(next),
    isActive: (key) => () => order().some((e) => e.key === key),
    dirOf: (key) => () => order().find((e) => e.key === key)?.dir ?? "asc",
    indexOf: (key) => () => {
      const cur = order();
      if (cur.length < 2) return null;
      const i = cur.findIndex((e) => e.key === key);
      return i >= 0 ? i + 1 : null;
    },
  };
}
